import { useNavigate } from 'react-router-dom'
import { Lock, ArrowRight, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'

interface UpgradePromptProps {
  feature: string
  requiredPlan?: string
  title?: string
  description?: string
}

const FEATURE_INFO: Record<string, { title: string; description: string; plan: string }> = {
  finance: {
    title: 'Complete Finance',
    description: 'Access full general ledger, budgets, financial reports, and accounting features.',
    plan: 'starter',
  },
  completeFinance: {
    title: 'Advanced Accounting',
    description: 'Get complete GL, budgets, bank reconciliation, and advanced financial reporting.',
    plan: 'growth',
  },
  attendance: {
    title: 'Attendance Tracking',
    description: 'Track service attendance, manage check-ins, and monitor member engagement.',
    plan: 'starter',
  },
  qrAttendance: {
    title: 'QR Code Attendance',
    description: 'Enable quick check-in with QR codes for faster and more accurate attendance tracking.',
    plan: 'growth',
  },
  hrPayroll: {
    title: 'HR & Payroll',
    description: 'Manage employees, process payroll, handle statutory deductions, and generate HR reports.',
    plan: 'leadership',
  },
  mediaLibrary: {
    title: 'Media Library',
    description: 'Store and manage sermons, photos, videos, and other media files for your church.',
    plan: 'growth',
  },
  multiLocation: {
    title: 'Multi-Campus Support',
    description: 'Manage multiple church locations from a single dashboard with unified reporting.',
    plan: 'leadership',
  },
  advancedMinistry: {
    title: 'Advanced Ministry',
    description: 'Access counseling management, ministry scheduling, and advanced discipleship tools.',
    plan: 'growth',
  },
  ministryScheduling: {
    title: 'Ministry Scheduling',
    description: 'Schedule volunteers, manage ministry teams, and coordinate service roles.',
    plan: 'growth',
  },
  mobileApp: {
    title: 'White-label Mobile App',
    description: 'Get a custom-branded mobile app for your church members.',
    plan: 'leadership',
  },
  apiAccess: {
    title: 'API Access',
    description: 'Integrate FaithYoke with your existing tools via our REST API.',
    plan: 'leadership',
  },
}

export default function UpgradePrompt({ feature, requiredPlan, title, description }: UpgradePromptProps) {
  const navigate = useNavigate()
  const info = FEATURE_INFO[feature]

  const displayTitle = title || info?.title || 'Premium Feature'
  const displayDescription = description || info?.description || 'This feature requires a higher plan.'
  const plan = requiredPlan || info?.plan || 'growth'

  return (
    <div className="flex items-center justify-center min-h-[400px] p-8">
      <Card className="max-w-md w-full border-dashed border-2">
        <CardContent className="pt-8 text-center">
          <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-4">
            <Lock className="h-8 w-8 text-amber-600 dark:text-amber-400" />
          </div>

          <h3 className="text-xl font-bold mb-2">{displayTitle}</h3>
          <p className="text-muted-foreground mb-6">{displayDescription}</p>

          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-6">
            <Sparkles className="h-4 w-4" />
            Available on the <strong className="capitalize">{plan}</strong> plan and above
          </div>

          <div className="flex flex-col gap-3">
            <Button
              onClick={() => navigate(`/checkout?plan=${plan}&billing=annual`)}
              className="bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600"
            >
              Upgrade to {plan.charAt(0).toUpperCase() + plan.slice(1)}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <Button variant="ghost" onClick={() => navigate('/pricing')}>
              Compare All Plans
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
